import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { DatabaseIcon, MinusSmIcon, PlusSmIcon } from '@heroicons/react/outline';

import { useFeature } from '../../../hooks/useFeature';
import * as Constants from '../../../utils/constants';
import * as BackupApi from '../../../api/connectionBackups.js';

import { TwPage } from '../../../components/TailwindPage';
import { Panel as CmpPanel } from '../../../components/Panel';
import { Button as CmpButton } from '../../../components/Button';
import { ButtonGroup as CmpButtonGroup } from '../../../components/ButtonGroup';
import { TwTable } from '../../../components/TailwindTable';
import { Search as CmpSearch } from '../../../components/Search';
import { Badge as CmpBadge, badgeTypes } from '../../../components/Badge';

const MIN_KEEP_COUNT = 1;
const MAX_KEEP_COUNT = 30;
// Mirrors ConnectionBackupSettings.MaxKeepCount: what a group gets until its retention is set to something else.
const DEFAULT_KEEP_COUNT = MAX_KEEP_COUNT;

const FILTERS = { all: 'all', on: 'on', off: 'off' };

const stepperInputClass = 'w-14 rounded-md border border-gray-300 bg-white py-1 px-2 text-center text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white';
const stepperButtonClass = 'flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:opacity-50 disabled:hover:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700';

const saveError = (err) => {
  if (err?.response?.status === 403)
    return 'You do not have access to change the backup settings.';
  return err?.response?.data?.Message || err?.response?.data?.message || 'The backup settings could not be saved. Try again.';
};

const clampKeepCount = (n) => Math.min(MAX_KEEP_COUNT, Math.max(MIN_KEEP_COUNT, Math.round(n)));

// A compact +/- numeric control. `onChange` only fires with an integer already clamped to [MIN_KEEP_COUNT, MAX_KEEP_COUNT].
// Typing is buffered locally and committed only on blur or Enter, not per keystroke: committing the default
// control on every keystroke would move its followers through every digit typed, not just the finished number.
// Blur always re-syncs the box to `value`, even when the typed number clamped back to what was already there
// (typing 45 over 30 stays 30) - otherwise the box would keep showing the rejected number forever.
const RetentionStepper = ({ id, value, disabled, ariaLabel, onChange }) => {
  const [text, setText] = useState(String(value));
  const decreaseRef = useRef(null);
  const increaseRef = useRef(null);

  useEffect(() => setText(String(value)), [value]);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed !== '') {
      const n = Number(trimmed);
      if (Number.isFinite(n))
        onChange(clampKeepCount(n));
    }
    setText(String(value));
  };

  // A disabled button can't hold focus, so move it to the still-enabled sibling before the browser drops it.
  const decrease = () => {
    const next = clampKeepCount(value - 1);
    onChange(next);
    if (next <= MIN_KEEP_COUNT)
      increaseRef.current?.focus();
  };

  const increase = () => {
    const next = clampKeepCount(value + 1);
    onChange(next);
    if (next >= MAX_KEEP_COUNT)
      decreaseRef.current?.focus();
  };

  return (
    <span className="inline-flex items-center gap-1">
      <button ref={decreaseRef} type="button" id={`${id}-decrease`} className={stepperButtonClass} disabled={disabled || value <= MIN_KEEP_COUNT}
        aria-label={`Decrease ${ariaLabel}`} onClick={decrease}>
        <MinusSmIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      <input id={id} type="number" inputMode="numeric" className={stepperInputClass} value={text} min={MIN_KEEP_COUNT} max={MAX_KEEP_COUNT}
        disabled={disabled} aria-label={ariaLabel} onChange={e => setText(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
      <button ref={increaseRef} type="button" id={`${id}-increase`} className={stepperButtonClass} disabled={disabled || value >= MAX_KEEP_COUNT}
        aria-label={`Increase ${ariaLabel}`} onClick={increase}>
        <PlusSmIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </span>
  );
};

export default function Config() {
  const { hasFeature } = useFeature();
  const canEdit = hasFeature(Constants.environmentsConfigurationFeatureCode);

  const [pageState, setPageState] = useState('loading');
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(FILTERS.all);
  const [defaultRetention, setDefaultRetention] = useState(DEFAULT_KEEP_COUNT);
  // Group ids that currently follow the default, tracked by id so stepping the default through a
  // customized group's value doesn't absorb it (see changeKeepCount). Re-derived on every load from
  // literal equality with DEFAULT_KEEP_COUNT, never guessed from whatever value is merely most common:
  // a guess can call a customized group "default" because enough groups happen to share its value,
  // and then silently overwrite it on the next default move. Equality can under-count after a save
  // moves the true followers away from 30 (they read as customized on reload), but it can't over-count,
  // and under-counting - losing a badge - is the safe direction to be wrong in.
  const [following, setFollowing] = useState(new Set());
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const load = async () => {
    setPageState('loading');
    try {
      const settings = await BackupApi.getBackupSettings();
      if (!mounted.current)
        return;
      setRows(settings);
      setSaved(settings);
      setDefaultRetention(DEFAULT_KEEP_COUNT);
      setFollowing(new Set(settings.filter(r => r.keepCount === DEFAULT_KEEP_COUNT).map(r => r.environmentGroupId)));
      setPageState('loaded');
    } catch {
      if (mounted.current)
        setPageState('error');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const change = (groupId, patch) => setRows(current => current.map(r => (r.environmentGroupId === groupId ? { ...r, ...patch } : r)));

  // A person editing a group's own retention takes it off the shared default, even if the value they land on
  // happens to match it right now - it no longer tracks future moves of the default.
  const changeKeepCount = (groupId, keepCount) => {
    setFollowing(current => {
      if (!current.has(groupId))
        return current;
      const next = new Set(current);
      next.delete(groupId);
      return next;
    });
    change(groupId, { keepCount });
  };

  const changed = JSON.stringify(rows) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await BackupApi.saveBackupSettings(rows);
      if (!mounted.current)
        return;
      setSaved(rows);
      toast.success('Backup settings saved');
    } catch (err) {
      if (mounted.current)
        setError(saveError(err));
    } finally {
      if (mounted.current)
        setSaving(false);
    }
  };

  // Moving the shared default carries along every group that still follows it; a group a person took off
  // the default (see changeKeepCount) keeps its own value regardless of where the default lands.
  const changeDefaultRetention = (next) => {
    setRows(current => current.map(r => (following.has(r.environmentGroupId) ? { ...r, keepCount: next } : r)));
    setDefaultRetention(next);
  };

  const turnAllOn = () => setRows(current => current.map(r => ({ ...r, enabled: true })));
  const turnAllOff = () => setRows(current => current.map(r => ({ ...r, enabled: false })));

  const onCount = rows.filter(r => r.enabled).length;
  const offCount = rows.length - onCount;

  const visibleRows = rows.filter(r => {
    if (filter === FILTERS.on && !r.enabled)
      return false;
    if (filter === FILTERS.off && r.enabled)
      return false;
    const query = search.trim().toLowerCase();
    return !query || r.environmentGroupName.toLowerCase().includes(query);
  });

  return (
    <div data-state={pageState === 'loaded' && saving ? 'saving' : pageState}>
      <TwPage>
        <TwPage.Header>
          <TwPage.Header.Hdr.Fixed title="Admin" description="Settings that apply to every environment of an environment group." />
        </TwPage.Header>

        <CmpPanel>
          {pageState === 'loading' && (
            <div className="space-y-4" role="status" aria-busy="true" aria-label="Loading">
              <span className="skeleton-loader w-1/3" />
              <span className="skeleton-loader w-2/3" />
            </div>
          )}

          {pageState === 'error' && (
            <div role="alert" className="rounded-md bg-red-50 p-4 dark:bg-red-900">
              <p className="text-sm text-red-800 dark:text-red-100">The backup settings could not be loaded.</p>
              <div className="mt-3">
                <CmpButton id="config-retry" variant={CmpButton.variants.secondary} onClick={load}>Retry</CmpButton>
              </div>
            </div>
          )}

          {pageState === 'loaded' && (
            <div>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-200">
                    <DatabaseIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200">Daily backups</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                      Workflow and SmartForm connections of each environment are backed up once a day. A new backup is only kept when the connections changed since the previous one.
                    </p>
                  </div>
                </div>
                {rows.length > 0 && (
                  <span id="config-backup-count" aria-live="polite" className="inline-flex flex-shrink-0 items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <span className={`h-2 w-2 rounded-full ${onCount > 0 ? 'bg-green-500' : 'bg-gray-400'}`} aria-hidden="true" />
                    {`${onCount} of ${rows.length} groups backed up`}
                  </span>
                )}
              </div>

              {!canEdit && (
                <p role="status" className="mt-4 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
                  You can see the settings, but changing them needs Environments configuration access.
                </p>
              )}

              {rows.length === 0 && <p id="no-groups" className="mt-4 text-sm text-gray-600 dark:text-gray-400">There are no environment groups to back up.</p>}

              {rows.length > 0 && (
                <>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-200">Default retention</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Applies to every group without a custom value.</p>
                    </div>
                    <label htmlFor="config-default-retention" className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-200">
                      Keep the last
                      <RetentionStepper id="config-default-retention" value={defaultRetention} disabled={!canEdit || saving}
                        ariaLabel="the default retention" onChange={changeDefaultRetention} />
                      backups per environment
                    </label>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div className="flex flex-wrap items-center gap-3">
                      <CmpSearch id="config-search" name="config-search" label="Search environment groups" placeholder="Search environment groups"
                        onChange={e => setSearch(e.target.value)} />
                      <CmpButtonGroup buttons={[
                        { content: `All ${rows.length}`, current: filter === FILTERS.all, togglable: true, onClick: () => setFilter(FILTERS.all) },
                        { content: `On ${onCount}`, current: filter === FILTERS.on, togglable: true, onClick: () => setFilter(FILTERS.on) },
                        { content: `Off ${offCount}`, current: filter === FILTERS.off, togglable: true, onClick: () => setFilter(FILTERS.off) },
                      ]} />
                    </div>
                    <span className="flex gap-2">
                      <CmpButton id="config-turn-all-on" variant={CmpButton.variants.secondary} disabled={!canEdit || saving || rows.every(r => r.enabled)}
                        onClick={turnAllOn}>Turn all on</CmpButton>
                      <CmpButton id="config-turn-all-off" variant={CmpButton.variants.secondary} disabled={!canEdit || saving || rows.every(r => !r.enabled)}
                        onClick={turnAllOff}>Turn all off</CmpButton>
                    </span>
                  </div>

                  {visibleRows.length === 0 && <p id="no-matches" className="mt-4 text-sm text-gray-600 dark:text-gray-400">No environment group matches the search and filter.</p>}

                  {visibleRows.length > 0 && (
                    <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                      <TwTable additionalClass="mt-0">
                        <TwTable.Head>
                          <TwTable.Header>Backup</TwTable.Header>
                          <TwTable.Header>Environment group</TwTable.Header>
                          <TwTable.Header>Retention</TwTable.Header>
                        </TwTable.Head>
                        <TwTable.Body>
                          {visibleRows.map(row => (
                            <TwTable.BodyRow key={row.environmentGroupId} useHover={false}>
                              <TwTable.BodyCol additionalClass="pl-4">
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                  {/* disabled alone isn't trusted to block a click: guard onChange too. The visible On/Off
                                      is aria-hidden so a screen reader isn't told the state twice (once as that text,
                                      once as the checkbox's own checked/unchecked). */}
                                  <input id={`backup-checkbox-${row.environmentGroupId}`} type="checkbox" checked={row.enabled}
                                    disabled={!canEdit || saving} aria-label={`${row.environmentGroupName} backups`}
                                    onChange={e => canEdit && !saving && change(row.environmentGroupId, { enabled: e.target.checked })}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                                  <span aria-hidden="true">{row.enabled ? 'On' : 'Off'}</span>
                                </label>
                              </TwTable.BodyCol>
                              <TwTable.BodyCol additionalClass="text-gray-900 dark:text-gray-200">{row.environmentGroupName}</TwTable.BodyCol>
                              <TwTable.BodyCol>
                                <span className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-200">
                                  <RetentionStepper id={`backup-keep-${row.environmentGroupId}`} value={row.keepCount}
                                    disabled={!canEdit || saving || !row.enabled} ariaLabel={`retention for ${row.environmentGroupName}`}
                                    onChange={n => changeKeepCount(row.environmentGroupId, n)} />
                                  backups
                                  {/* Shown even for a group that's off: it says what retention applies if it's turned back on. */}
                                  {following.has(row.environmentGroupId) && <CmpBadge type={badgeTypes.default} text="Default" />}
                                </span>
                              </TwTable.BodyCol>
                            </TwTable.BodyRow>
                          ))}
                        </TwTable.Body>
                      </TwTable>
                    </div>
                  )}

                  <p id="config-showing-count" className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {`Showing ${visibleRows.length} of ${rows.length} environment groups`}
                  </p>
                </>
              )}

              {error && <div id="config-error" role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900 dark:text-red-100">{error}</div>}

              {canEdit && rows.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <CmpButton id="config-save" variant={CmpButton.variants.primary} disabled={!changed || saving} onClick={save}>
                    {saving ? 'Saving…' : 'Save'}
                  </CmpButton>
                </div>
              )}
            </div>
          )}
        </CmpPanel>
      </TwPage>
    </div>
  );
}
